import type { SalonSearchResult } from '../api/use-marketplace';

/** [southWest[lat,lng], northEast[lat,lng]] — Leaflet LatLngBoundsExpression. */
export type Bounds = [[number, number], [number, number]];

/** Results that have a primary location with real coordinates (map-plottable). */
export function plottableResults(results: SalonSearchResult[]): SalonSearchResult[] {
  return results.filter(
    (r) => r.location != null && r.location.lat != null && r.location.lng != null,
  );
}

/**
 * Bounding box covering every plottable salon plus the user's position (if any).
 * Null when there is nothing to show. A single point yields a degenerate
 * [[p],[p]] box (the caller applies a min-zoom).
 */
export function boundsFor(
  results: SalonSearchResult[],
  userCoords: { lat: number; lng: number } | null,
): Bounds | null {
  const pts: Array<[number, number]> = plottableResults(results).map((r) => [
    r.location!.lat as number,
    r.location!.lng as number,
  ]);
  if (userCoords) pts.push([userCoords.lat, userCoords.lng]);
  if (pts.length === 0) return null;
  let minLat = pts[0][0], maxLat = pts[0][0], minLng = pts[0][1], maxLng = pts[0][1];
  for (const [la, ln] of pts) {
    minLat = Math.min(minLat, la); maxLat = Math.max(maxLat, la);
    minLng = Math.min(minLng, ln); maxLng = Math.max(maxLng, ln);
  }
  return [[minLat, minLng], [maxLat, maxLng]];
}

/** Human distance label; '' when unknown, '<1 km' under a km, else '2.4 km'. */
export function formatDistanceKm(distanceKm: number | null): string {
  if (distanceKm == null) return '';
  if (distanceKm < 1) return '<1 km';
  return `${distanceKm.toFixed(1)} km`;
}
